(() => {
  window.__VariantOptionModalsLoaded = true;

  const INIT_ATTR = "data-variant-modals-inited";
  const OPEN_CLASS = "is-open";
  const ACTIVE_CLASS = "ModelCard--active";
  const SOLDOUT_CLASS = "ModelCard--soldout";
  const DISABLED_CLASS = "is-disabled";
  const TRANSITION_MS = 260;

  const AUTO_OPEN_NEXT_MODAL = false;

  const lockScroll = () => {
    document.documentElement.classList.add("modal-open");
    document.body.classList.add("modal-open");
  };
  const unlockScroll = () => {
    document.documentElement.classList.remove("modal-open");
    document.body.classList.remove("modal-open");
  };

  const initRoot = (root) => {
    if (!root || root.hasAttribute(INIT_ATTR)) return;
    root.setAttribute(INIT_ATTR, "true");

    const variantsEl = root.querySelector("[data-variant-modals-variants]");
    if (!variantsEl) return;

    const variants = JSON.parse(variantsEl.textContent || "[]");
    const baseOptionPosition = Number(
      root.getAttribute("data-base-option-position") || "",
    );
    let isApplyingAutoFallback = false;
    const fallbackOptionPosition = 2;
    const fallbackOptionValues = Array.from(
      root.querySelectorAll(
        `[data-option-block][data-option-position="${fallbackOptionPosition}"] [data-option-pick]`,
      ),
    )
      .map((button) => button.getAttribute("data-option-value") || "")
      .filter(Boolean);

    // ==== Focal inputは form="..." で紐づくことがあるので、document検索 + form絞り込み ====
    const getFormId = () => root.getAttribute("data-form-id") || "";
    const getFormEl = () => {
      const formId = getFormId();
      return formId ? document.getElementById(formId) : null;
    };

    const qInForm = (selector) => {
      const formId = getFormId();
      if (!formId) return null;
      return document.querySelector(
        `${selector}[form="${CSS.escape(formId)}"]`,
      );
    };

    const qAllInForm = (selector) => {
      const formId = getFormId();
      if (!formId) return [];
      return Array.from(
        document.querySelectorAll(`${selector}[form="${CSS.escape(formId)}"]`),
      );
    };

    const dispatchNativeEvents = (el) => {
      el.dispatchEvent(new Event("input", { bubbles: true }));
      el.dispatchEvent(new Event("change", { bubbles: true }));
    };

    const getCartFormIdInput = () => {
      const formEl = getFormEl();
      return formEl?.querySelector('[name="id"]') || null;
    };

    const syncVariantDebug = (variant) => {
      const debug = root.querySelector("[data-variant-debug]");
      if (!debug) return;

      const formIdValue = getCartFormIdInput()?.value || "-";
      const eventIdValue = variant?.id ? String(variant.id) : "-";
      const urlVariantValue = new URL(window.location.href).searchParams.get("variant") || "-";

      const formIdEl = debug.querySelector("[data-variant-debug-form-id]");
      const eventIdEl = debug.querySelector("[data-variant-debug-event-id]");
      const urlEl = debug.querySelector("[data-variant-debug-url]");
      if (formIdEl) formIdEl.textContent = formIdValue;
      if (eventIdEl) eventIdEl.textContent = eventIdValue;
      if (urlEl) urlEl.textContent = urlVariantValue;
    };

    const getSaleStatusForVariant = (variant) => {
      if (!variant) return "default";

      const metafields = variant.metafields || {};
      const now = new Date();
      const saleStart = toDate(metafields.sale_start);
      const saleEnd = toDate(metafields.sale_end);
      const inventoryQuantity = Number(variant.inventory_quantity ?? 0);
      const syohinKbnRaw = metafields.syohin_kbn;
      const toriatukaiKbnRaw = metafields.toriatukai_kbn;
      const syohinKbn = Number(syohinKbnRaw || 0);
      const toriatukaiKbn = Number(toriatukaiKbnRaw || 0);
      const isSaleStartPresent = !isBlank(metafields.sale_start);
      const isSyohinValid = !isBlank(syohinKbnRaw) && (syohinKbn === 0 || syohinKbn === 10);
      const isToriatukaiValid =
        !isBlank(toriatukaiKbnRaw) &&
        (toriatukaiKbn === 0 || toriatukaiKbn === 1 || toriatukaiKbn === 2);
      const isBeforeSale = !saleStart || saleStart.getTime() > now.getTime();
      const isAfterSaleEnd = !!saleEnd && saleEnd.getTime() < now.getTime();
      const isStockOut = inventoryQuantity <= 0;
      const isToriatukaiStopped = toriatukaiKbn === 1 || toriatukaiKbn === 2;

      if (!isSaleStartPresent || !isSyohinValid || !isToriatukaiValid) return "preparing";
      if (isBeforeSale) return "before_sale";
      if (isAfterSaleEnd && isToriatukaiStopped) return "unavailable";
      if (isAfterSaleEnd && toriatukaiKbn === 0) return "closed";
      if (isStockOut && isToriatukaiStopped) return "unavailable";
      if (isStockOut && toriatukaiKbn === 0) return "stock_out_notify";
      if (syohinKbn === 10) return "pre_order";
      if (syohinKbn === 0) return "normal";

      return "default";
    };

    const syncPaymentStatusToVariant = (variant) => {
      const formEl = getFormEl();
      const paymentContainer = formEl?.querySelector(
        "product-payment-container[data-hm-sale-status]",
      );
      if (!paymentContainer) return;

      paymentContainer.setAttribute("data-hm-sale-status", getSaleStatusForVariant(variant));
    };

    const enforceCanonicalSelectedVariant = () => {
      const selected = getSelectedOptions();
      const exactVariant = findExactVariant(selected);
      const idInput = getCartFormIdInput();
      if (!exactVariant?.id || !idInput) return null;

      const exactVariantId = String(exactVariant.id);
      const didChange = String(idInput.value || "") !== exactVariantId;
      idInput.value = exactVariantId;
      if (didChange) {
        dispatchNativeEvents(idInput);
      }

      syncPaymentStatusToVariant(exactVariant);
      syncVariantDebug(exactVariant);
      return exactVariant;
    };

    // ==== 元の variant-picker を特定（隠しているFocal UIを操作するため） ====
    const getVariantPickerEl = () => {
      const formId = getFormId();
      if (!formId) return null;
      return document.querySelector(
        `variant-picker[form-id="${CSS.escape(formId)}"]`,
      );
    };

    // ==== option position に対応する「元UIの option selector ブロック」を探す ====
    const findOriginalOptionSelector = (pos) => {
      const vp = getVariantPickerEl();
      if (!vp) return null;

      // option selector内の input を起点にする（swatch/block/variant_image/dropdown いずれも拾える）
      const marker =
        vp.querySelector(
          `.product-form__option-selector input[data-option-position="${pos}"]`,
        ) ||
        vp.querySelector(
          `.product-form__option-selector input[name="option${pos}"]`,
        );

      return marker ? marker.closest(".product-form__option-selector") : null;
    };

    // ==== dropdown(combo-box) の該当 option を「クリック」で選択する（これが端末用の肝） ====
    const selectViaComboBox = (pos, valueLabel, valueId = "", { silent = false } = {}) => {
      const wrap = findOriginalOptionSelector(pos);
      if (!wrap) return false;

      const combo = wrap.querySelector("combo-box");
      if (!combo) return false;

      // Focalのcombo-box候補：button[role=option]（同一商品内切替） / a（別商品遷移）
      const buttons = Array.from(
        combo.querySelectorAll('button[role="option"]'),
      );
      const hitBtn =
        (valueId &&
          buttons.find((b) => String(b.value || "") === String(valueId))) ||
        buttons.find(
          (b) => (b.textContent || "").trim() === String(valueLabel).trim(),
        );
      if (hitBtn) {
        const nextValue = hitBtn.value || valueId || valueLabel;
        const nativeControl =
          combo.querySelector('input[type="hidden"]') ||
          combo.querySelector("select");
        if (!nativeControl) return false;

        if ("selectedValue" in combo) {
          combo.selectedValue = nextValue;
        }
        nativeControl.value = nextValue;
        nativeControl.setAttribute("data-option-value", valueLabel);
        if (!silent) {
          dispatchNativeEvents(nativeControl);
        }
        return true;
      }

      // aしか無い場合＝別商品遷移設計。今回は「同一商品内でoption2同様に」なので不可
      const links = Array.from(
        combo.querySelectorAll("a.combo-box__option-item"),
      );
      const hitA = links.find(
        (a) => (a.textContent || "").trim() === String(valueLabel).trim(),
      );
      if (hitA) {
        console.warn(
          "[variant-modals] option is link-type (product_url). Same-product variant switching is not possible for this option without redesign.",
          { pos, valueLabel, href: hitA.getAttribute("href") },
        );
        return false;
      }

      return false;
    };

    const getSelectedOptions = () => {
      const selected = {};
      for (let pos = 1; pos <= 3; pos++) {
        const checkedRadio = qInForm(
          `input[type="radio"][name="option${pos}"]:checked`,
        );
        if (checkedRadio) {
          const v = checkedRadio.getAttribute("data-option-value") || "";
          if (v) selected[pos] = v;
          continue;
        }

        const hidden = qInForm(
          `input[type="hidden"][name="option${pos}"][data-option-position="${pos}"]`,
        );
        if (hidden) {
          const v =
            hidden.getAttribute("data-option-value") || hidden.value || "";
          if (v) selected[pos] = v;
        }
      }

      if (baseOptionPosition && !selected[baseOptionPosition]) {
        const baseBlock = root.querySelector(
          `[data-option-block][data-option-position="${baseOptionPosition}"]`,
        );
        const baseLabel =
          baseBlock?.querySelector("[data-option-selected-label]")?.textContent?.trim() ||
          "";
        if (baseLabel && baseLabel !== "選択してください") {
          selected[baseOptionPosition] = baseLabel;
        }
      }

      return selected;
    };

    // ==== 最終：setOptionValue（radio → combo-box → hidden fallback） ====
    const setOptionValue = (pos, value, valueId = "", { silent = false } = {}) => {
      // 1) radio（swatch/block/variant_image）
      const radio =
        (valueId &&
          qInForm(
            `input[type="radio"][name="option${pos}"][value="${CSS.escape(valueId)}"]`,
          )) ||
        qInForm(
          `input[type="radio"][name="option${pos}"][data-option-value="${CSS.escape(value)}"]`,
        );
      if (radio) {
        radio.checked = true;
        if (!silent) {
          dispatchNativeEvents(radio);
        }
        return true;
      }

      // 2) dropdown（combo-box）: これが option1（端末）の典型
      if (selectViaComboBox(pos, value, valueId, { silent })) {
        return true;
      }

      // 3) hidden fallback（拾われない場合もあるが一応）
      const hidden = qInForm(
        `input[type="hidden"][name="option${pos}"][data-option-position="${pos}"]`,
      );
      if (hidden) {
        hidden.setAttribute("data-option-value", value);
        hidden.value = valueId || value;
        if (!silent) {
          dispatchNativeEvents(hidden);
        }
        return true;
      }

      return false;
    };

    const buildFilterSelection = (posToCheck, valueToCheck, selected) => {
      if (!baseOptionPosition) {
        return { ...selected, [posToCheck]: valueToCheck };
      }

      if (posToCheck === baseOptionPosition) {
        return { [posToCheck]: valueToCheck };
      }

      const nextSelected = { [posToCheck]: valueToCheck };
      const selectedBaseValue = selected[baseOptionPosition];

      if (selectedBaseValue) {
        nextSelected[baseOptionPosition] = selectedBaseValue;
      }

      return nextSelected;
    };

    const findMatchVariants = (posToCheck, valueToCheck, selected) => {
      const nextSelected = buildFilterSelection(posToCheck, valueToCheck, selected);
      return variants.filter((v) => {
        if (!Array.isArray(v.options)) return false;
        for (const [posStr, val] of Object.entries(nextSelected)) {
          const pos = Number(posStr);
          const optVal = v.options[pos - 1];
          if (optVal == null) return false;
          if (String(optVal) !== String(val)) return false;
        }
        return true;
      });
    };

    const findExactVariant = (selected) => {
      return (
        variants.find((variant) => {
          if (!Array.isArray(variant?.options)) return false;

          for (const [posStr, val] of Object.entries(selected)) {
            const pos = Number(posStr);
            if (!pos) return false;
            if (String(variant.options[pos - 1] || "") !== String(val)) return false;
          }

          return true;
        }) || null
      );
    };

    const findFallbackVariantForBase = (baseValue) => {
      if (!baseOptionPosition || !baseValue) return null;

      const candidates = variants.filter((variant) => {
        return (
          Array.isArray(variant?.options) &&
          String(variant.options[baseOptionPosition - 1] || "") === String(baseValue)
        );
      });
      if (!candidates.length) return null;

      for (const optionValue of fallbackOptionValues) {
        const matchedByOption2 = candidates.filter((variant) => {
          return String(variant.options[fallbackOptionPosition - 1] || "") === String(optionValue);
        });
        if (!matchedByOption2.length) continue;

        return matchedByOption2.find((variant) => variant.available) || matchedByOption2[0];
      }

      return candidates.find((variant) => variant.available) || candidates[0];
    };

    const syncOptionsToVariant = (variant, { skipPosition, silent = false } = {}) => {
      if (!Array.isArray(variant?.options)) return;

      variant.options.forEach((optionValue, index) => {
        const pos = index + 1;
        if (!optionValue || pos === skipPosition) return;
        setOptionValue(pos, optionValue, "", { silent });
      });
    };

    const resolveVariantForPick = (pick) => {
      const pos = Number(pick?.getAttribute("data-option-position"));
      const value = pick?.getAttribute("data-option-value") || "";
      if (!pos || !value) return null;

      const selectedBeforeChange = getSelectedOptions();
      if (pos === baseOptionPosition) {
        const requestedSelection = { ...selectedBeforeChange, [pos]: value };
        return findExactVariant(requestedSelection) || findFallbackVariantForBase(value);
      }

      const requestedSelection = { ...selectedBeforeChange, [pos]: value };
      const matchingVariants = findMatchVariants(pos, value, selectedBeforeChange);
      return (
        findExactVariant(requestedSelection) ||
        matchingVariants.find((variant) => variant?.available) ||
        matchingVariants[0] ||
        null
      );
    };

    const applyVariantFallbackIfNeeded = () => {
      if (isApplyingAutoFallback || !baseOptionPosition) return;

      const selected = getSelectedOptions();
      const baseValue = selected[baseOptionPosition];
      if (!baseValue) return;

      const exactVariant = findExactVariant(selected);
      if (exactVariant) return;

      const fallbackVariant = findFallbackVariantForBase(baseValue);
      if (!fallbackVariant) return;

      isApplyingAutoFallback = true;
      syncOptionsToVariant(fallbackVariant, { skipPosition: baseOptionPosition });
      window.setTimeout(() => {
        isApplyingAutoFallback = false;
      }, 0);
    };

    const getVariantFromFragment = (htmlFragment) => {
      if (!htmlFragment) return null;

      const variantPicker = htmlFragment.querySelector(
        `variant-picker[form-id="${CSS.escape(getFormId())}"]`,
      );
      if (!variantPicker) return null;

      try {
        return JSON.parse(
          variantPicker.querySelector("script[data-variant]")?.textContent || "{}",
        );
      } catch (_error) {
        return null;
      }
    };

    const preserveCurrentMetaInFragment = (htmlFragment) => {
      if (!htmlFragment) return;

      const currentMeta = document.querySelector(
        `form#${CSS.escape(getFormId())} [data-block-type="meta"]`,
      );
      if (!currentMeta) return;

      const nextMeta = htmlFragment.querySelector(
        `form#${CSS.escape(getFormId())} [data-block-type="meta"]`,
      );
      if (!nextMeta) return;

      nextMeta.replaceWith(currentMeta.cloneNode(true));
    };

    const replaceRootFromFragment = (htmlFragment) => {
      const formId = getFormId();
      if (!formId || !htmlFragment || !document.documentElement.contains(root)) {
        return null;
      }

      const nextRoot = htmlFragment.querySelector(
        `[data-variant-modals-root][data-form-id="${CSS.escape(formId)}"]`,
      );
      if (!nextRoot) return null;

      root.replaceWith(nextRoot);
      unlockScroll();
      initRoot(nextRoot);
      return nextRoot;
    };

    const findFirstExistingOptionButton = (nextRoot) => {
      if (!nextRoot || !baseOptionPosition) return null;

      const optionBlocks = Array.from(nextRoot.querySelectorAll("[data-option-block]"));
      const nextOptionBlock =
        optionBlocks.find((block) => {
          const pos = Number(block.getAttribute("data-option-position"));
          return pos && pos !== baseOptionPosition;
        }) || null;

      return (
        nextOptionBlock?.querySelector(
          '[data-option-pick][data-option-has-variant="true"]',
        ) || null
      );
    };

    const applyOfficialFallbackFromRoot = (nextRoot) => {
      if (isApplyingAutoFallback) return;

      const fallbackButton = findFirstExistingOptionButton(nextRoot);
      if (!fallbackButton) return;

      const pos = Number(fallbackButton.getAttribute("data-option-position"));
      const value = fallbackButton.getAttribute("data-option-value") || "";
      const valueId = fallbackButton.getAttribute("data-option-value-id") || "";
      if (!pos || !value) return;

      isApplyingAutoFallback = true;
      setOptionValue(pos, value, valueId);
      window.setTimeout(() => {
        isApplyingAutoFallback = false;
      }, 0);
    };

    const toDate = (v) => {
      if (!v) return null;
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? null : d;
    };

    const isBlank = (v) => v === null || v === undefined || String(v).trim() === "";

    const isPreparingVariant = (variant) => {
      const metafields = variant?.metafields || {};
      return (
        isBlank(metafields.sale_start) ||
        isBlank(metafields.syohin_kbn) ||
        isBlank(metafields.toriatukai_kbn)
      );
    };

    const isBeforeSaleVariant = (variant, now) => {
      const saleStart = toDate(variant?.metafields?.sale_start);
      return !!saleStart && now.getTime() < saleStart.getTime();
    };

    const hasOemCartVariant = (variant) => {
      return !isBlank(variant?.metafields?.oem_cart);
    };

    const isPreOrderOpenVariant = (variant, now) => {
      const saleStart = toDate(variant?.metafields?.sale_start);
      const inventoryQuantity = Number(variant?.inventory_quantity ?? 0);
      const syohinKbn = Number(variant?.metafields?.syohin_kbn);
      return (
        inventoryQuantity > 0 &&
        syohinKbn === 10 &&
        !!saleStart &&
        saleStart.getTime() <= now.getTime()
      );
    };

    const isAfterSaleEndVariant = (variant, now) => {
      const saleEnd = toDate(variant?.metafields?.sale_end);
      return !!saleEnd && saleEnd.getTime() < now.getTime();
    };

    const isToriatukaiStoppedVariant = (variant) => {
      const kbn = Number(variant?.metafields?.toriatukai_kbn || 0);
      return kbn === 1 || kbn === 2;
    };

    const decideOverlayLabel = ({ matches }) => {
      // matches: その option 値に一致する候補バリアント配列（他option選択も加味済み）
      // ここで「1つだけ」文字を返す
      const now = new Date();
      const anyInStock = matches.some((v) => {
        const q = Number(v?.inventory_quantity ?? 0);
        return v?.available === true && q > 0;
      });

      if (matches.some(isPreparingVariant)) {
        return "準備中";
      }

      if (matches.some((v) => isBeforeSaleVariant(v, now))) {
        return "";
      }

      if (matches.some(hasOemCartVariant)) {
        return "";
      }

      // ① 販売終了（最優先）: 取扱終了、または受付終了後に取扱終了している場合
      if (
        matches.some((v) => {
          return isToriatukaiStoppedVariant(v) && (!anyInStock || isAfterSaleEndVariant(v, now));
        })
      ) {
        return "販売終了";
      }

      // ② 受付終了
      if (
        matches.some((v) => {
          const kbn = Number(v?.metafields?.toriatukai_kbn || 0);
          return kbn === 0 && isAfterSaleEndVariant(v, now);
        })
      ) {
        return "受付終了";
      }

      // ③ 予約販売中
      if (matches.some((v) => isPreOrderOpenVariant(v, now))) {
        return "予約";
      }

      // ④ 在庫切れ（在庫0）
      // matches が存在していて、1つも在庫が無いなら在庫切れ
      if (matches.length > 0) {
        if (!anyInStock) return "在庫切れ";
      }

      return "";
    };

    const refreshModalStates = (modal) => {
      const pos = Number(modal.getAttribute("data-option-position"));
      const isBaseOption = !!baseOptionPosition && pos === baseOptionPosition;
      const selected = getSelectedOptions();
      const currentVal = selected[pos] || "";
      const selectedBaseValue = baseOptionPosition
        ? selected[baseOptionPosition] || ""
        : "";
      const shouldHideUnavailableByBase =
        !!baseOptionPosition &&
        pos !== baseOptionPosition &&
        !!selectedBaseValue;

      modal.querySelectorAll("[data-option-pick]").forEach((btn) => {
        const value = btn.getAttribute("data-option-value") || "";
        const matches = findMatchVariants(pos, value, selected);
        const item = btn.closest(".ModelModal__item");
        const shouldHide =
          shouldHideUnavailableByBase &&
          matches.length === 0;

        if (item) {
          item.hidden = shouldHide;
        }

        if (shouldHide) {
          btn.classList.remove(ACTIVE_CLASS, SOLDOUT_CLASS, DISABLED_CLASS);
          btn.disabled = false;
          return;
        }

        const overlay = btn.querySelector("[data-thumb-overlay]");
        if (overlay) {
          const label = decideOverlayLabel({ matches });
          overlay.textContent = label;
          overlay.classList.toggle("is-visible", !!label);
          overlay.classList.toggle("is-pre-order", label === "予約");
          overlay.classList.toggle("is-sold-out", label === "在庫切れ");
          overlay.classList.toggle("is-ended", label === "販売終了");
          overlay.classList.toggle("is-closed", label === "受付終了");
        }

        const exists = isBaseOption
          ? true
          : matches.length > 0;
        const anyAvailable = isBaseOption
          ? true
          : matches.some((v) => !!v.available);
        const isActive = currentVal && String(currentVal) === String(value);

        btn.classList.toggle(ACTIVE_CLASS, !!isActive);
        btn.classList.remove(SOLDOUT_CLASS);
        btn.classList.remove(DISABLED_CLASS);
        btn.disabled = false;

        const state = btn.querySelector("[data-state]");
        const badge = btn.querySelector("[data-badge]");
        if (state) state.textContent = "";
        if (badge) badge.textContent = "";

        if (!exists) {
          btn.classList.add(DISABLED_CLASS);
          btn.disabled = true;
          if (badge) badge.textContent = "選択不可";
          return;
        }

        if (!anyAvailable) {
          btn.classList.add(SOLDOUT_CLASS);
          if (state) state.textContent = "在庫なし";
          if (badge) badge.textContent = "売り切れ";
          return;
        }

        if (isActive) {
          if (state) state.textContent = "選択中";
        }
      });
    };

    const getAutoModalColumnLimit = () => {
      if (window.matchMedia("(max-width: 600px)").matches) return 3;
      if (window.matchMedia("(max-width: 960px)").matches) return 5;
      return 8;
    };

    const updateAutoModalColumns = (modal) => {
      const panel = modal.querySelector(".ModelModal__panel--auto");
      if (!panel) return;

      const visibleCount = Array.from(modal.querySelectorAll(".ModelModal__item")).filter(
        (item) => !item.hidden,
      ).length;
      const columns = Math.max(1, Math.min(visibleCount || 1, getAutoModalColumnLimit()));
      panel.style.setProperty("--model-modal-columns", columns);
    };

    const openModal = (modal) => {
      refreshModalStates(modal);
      updateAutoModalColumns(modal);
      modal.hidden = false;
      requestAnimationFrame(() => modal.classList.add(OPEN_CLASS));
      lockScroll();
    };

    const closeModal = (modal) => {
      modal.classList.remove(OPEN_CLASS);
      unlockScroll();
      window.setTimeout(() => (modal.hidden = true), TRANSITION_MS);
    };

    const closeAllInRoot = () => {
      root.querySelectorAll("[data-option-modal]").forEach((m) => {
        if (!m.hidden) closeModal(m);
      });
    };

    const openNextModalIfNeeded = (currentPos) => {
      if (!AUTO_OPEN_NEXT_MODAL) return;
      const nextPos = Number(currentPos) + 1;
      const nextBlock = root.querySelector(
        `[data-option-block][data-option-position="${nextPos}"]`,
      );
      if (!nextBlock) return;
      const nextOpener = nextBlock.querySelector("[data-option-modal-open]");
      if (!nextOpener) return;
      const id = nextOpener.getAttribute("data-option-modal-open");
      const nextModal = root.querySelector(`#${CSS.escape(id)}`);
      if (!nextModal) return;
      openModal(nextModal);
    };

    // open
    root.addEventListener("click", (e) => {
      const opener = e.target.closest("[data-option-modal-open]");
      if (!opener) return;

      const id = opener.getAttribute("data-option-modal-open");
      const modal = root.querySelector(`#${CSS.escape(id)}`);
      if (!modal) return;

      openModal(modal);
    });

    // close
    root.addEventListener("click", (e) => {
      const closer = e.target.closest("[data-option-modal-close]");
      if (!closer) return;

      const modal = closer.closest("[data-option-modal]");
      if (!modal) return;

      closeModal(modal);
    });

    // pick
    root.addEventListener("click", (e) => {
      const pick = e.target.closest("[data-option-pick]");
      if (!pick) return;
      if (pick.disabled) return;

      const pos = Number(pick.getAttribute("data-option-position"));
      const value = pick.getAttribute("data-option-value") || "";
      const valueId = pick.getAttribute("data-option-value-id") || "";
      if (!pos || !value) return;

      const targetVariant = resolveVariantForPick(pick);
      if (targetVariant?.id) {
        syncOptionsToVariant(targetVariant, { skipPosition: pos, silent: true });
      }

      const ok = setOptionValue(pos, value, valueId);

      if (!ok) {
        console.warn("[variant-modals] setOptionValue failed", {
          pos,
          value,
          formId: getFormId(),
          radiosFound: qAllInForm(`input[type="radio"][name="option${pos}"]`)
            .length,
          hiddenFound: qAllInForm(
            `input[type="hidden"][name="option${pos}"][data-option-position="${pos}"]`,
          ).length,
          hasVariantPicker: !!getVariantPickerEl(),
          originalOptionSelectorFound: !!findOriginalOptionSelector(pos),
        });
        return;
      }

      // トリガー表示更新
      const block = root.querySelector(
        `[data-option-block][data-option-position="${pos}"]`,
      );
      if (block) {
        const label = block.querySelector("[data-option-selected-label]");
        if (label) label.textContent = value;
      }

      enforceCanonicalSelectedVariant();
      window.setTimeout(() => {
        const canonicalVariant = enforceCanonicalSelectedVariant();
        syncVariantDebug(canonicalVariant);
      }, 0);

      // 閉じる
      const modal = pick.closest("[data-option-modal]");
      if (modal) closeModal(modal);

      openNextModalIfNeeded(pos);
    });

    // change追従（このrootのformに紐づく input のみ）
    document.addEventListener("change", (e) => {
      const t = e.target;
      if (!(t instanceof HTMLInputElement)) return;

      const formId = getFormId();
      if (!formId) return;
      if (t.getAttribute("form") !== formId) return;

      const name = t.getAttribute("name") || "";
      if (!/^option[1-3]$/.test(name)) return;

      const selected = getSelectedOptions();
      const pos = Number(name.replace("option", ""));
      const val = selected[pos];
      if (val) {
        const block = root.querySelector(
          `[data-option-block][data-option-position="${pos}"]`,
        );
        if (block) {
          const label = block.querySelector("[data-option-selected-label]");
          if (label) label.textContent = val;
        }
      }

      root
        .querySelectorAll("[data-option-modal]:not([hidden])")
        .forEach((m) => {
          refreshModalStates(m);
          updateAutoModalColumns(m);
        });
    });

    // ESC
    document.addEventListener("keydown", (e) => {
      if (e.key !== "Escape") return;
      const anyOpen = root.querySelector(
        `[data-option-modal].${OPEN_CLASS}:not([hidden])`,
      );
      if (!anyOpen) return;
      closeAllInRoot();
    });

    window.addEventListener("resize", () => {
      root.querySelectorAll("[data-option-modal]:not([hidden])").forEach(updateAutoModalColumns);
    });

    const formatOptionValueForLabel = (optVal) => {
      if (optVal == null || optVal === "") return "";
      if (typeof optVal === "string" || typeof optVal === "number" || typeof optVal === "boolean") {
        return String(optVal);
      }
      if (typeof optVal === "object") {
        if (optVal.name != null) return String(optVal.name);
        if (optVal.value != null) return String(optVal.value);
      }
      return String(optVal);
    };

    const syncSelectorLabelsFromVariant = (variant) => {
      if (!variant || !Array.isArray(variant.options)) return;
      variant.options.forEach((optVal, index) => {
        const pos = index + 1;
        const block = root.querySelector(`[data-option-block][data-option-position="${pos}"]`);
        const label = block?.querySelector("[data-option-selected-label]");
        if (!label) return;
        const text = formatOptionValueForLabel(optVal);
        if (text) label.textContent = text;
      });
      root.querySelectorAll("[data-option-modal]:not([hidden])").forEach((m) => {
        refreshModalStates(m);
        updateAutoModalColumns(m);
      });
    };

    const formEl = getFormEl();
    formEl?.addEventListener("variant:changed", (e) => {
      if (!document.documentElement.contains(root)) return;

      const variant = e?.detail?.variant;
      const canonicalVariant = enforceCanonicalSelectedVariant();
      const effectiveVariant = canonicalVariant || variant;
      syncSelectorLabelsFromVariant(effectiveVariant);
      syncVariantDebug(effectiveVariant);

      if (!effectiveVariant?.id && !isApplyingAutoFallback) {
        window.setTimeout(applyVariantFallbackIfNeeded, 0);
      }
    });

    formEl?.addEventListener(
      "product:rerender",
      (event) => {
        if (!document.documentElement.contains(root)) return;

        const htmlFragment = event.detail?.htmlFragment;
        const nextVariant = getVariantFromFragment(htmlFragment);

        if (!nextVariant?.id) {
          preserveCurrentMetaInFragment(htmlFragment);
        }

        window.setTimeout(() => {
          const nextRoot = replaceRootFromFragment(htmlFragment);
          const canonicalVariant = enforceCanonicalSelectedVariant();
          syncVariantDebug(canonicalVariant || getVariantFromFragment(htmlFragment));

          if (!canonicalVariant?.id) {
            applyOfficialFallbackFromRoot(nextRoot);
          }
        }, 0);
      },
      true,
    );

    syncVariantDebug(findExactVariant(getSelectedOptions()));

    // ==== モーダル内バリアント画像の先読み ====
    // サムネイルは loading="lazy" かつ hidden（display:none）のモーダル内にあるため、
    // モーダルを開くまでブラウザは画像を取得しない。商品ページ表示後のアイドル時に
    // 非同期でキャッシュへ先読みしておき、初回オープン時のラグを無くす。
    const preloadModalThumbnails = () => {
      const imgs = root.querySelectorAll(".ModelCard__thumbImg");
      if (!imgs.length) return;
      const seen = new Set();
      imgs.forEach((img) => {
        const src = img.getAttribute("src");
        const srcset = img.getAttribute("srcset");
        const key = srcset || src;
        if (!key || seen.has(key)) return;
        seen.add(key);

        const pre = new Image();
        const sizes = img.getAttribute("sizes");
        if (srcset) pre.srcset = srcset;
        if (sizes) pre.sizes = sizes;
        if (src) pre.src = src;
      });
    };

    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(preloadModalThumbnails, { timeout: 2000 });
    } else {
      window.setTimeout(preloadModalThumbnails, 300);
    }
  };

  const initAll = () => {
    document.querySelectorAll("[data-variant-modals-root]").forEach(initRoot);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", initAll);
  document.addEventListener("shopify:section:reorder", initAll);
  document.addEventListener("shopify:section:select", initAll);

  const mo = new MutationObserver(() => initAll());
  mo.observe(document.documentElement, { childList: true, subtree: true });
})();
